import { DishAllergen } from "ts-packages/api-types/src";

import { useMemo } from "react";

import { Stack, Typography, useTheme } from "@mui/material";

import { NotInterestedRounded } from "@mui/icons-material";

import { AllergenChip, AllergenChipOutlined } from "igfd-ui/src";

interface AllergenListProps {
  allergens?: DishAllergen[];
  selectedAllergenIds: string[];
  mayContainAllergens?: DishAllergen[];
  onSelectAllergen: (allergen: DishAllergen) => void;
}

function AllergenSectionHeader({
  count,
  label,
}: {
  count: number;
  label: string;
}) {
  return (
    <Typography
      variant="labelSMedium"
      sx={{ color: useTheme().palette.oscar.light.content.content1 }}
    >
      {count} {label}
      {count > 1 ? "s" : ""}
    </Typography>
  );
}

function AllergenChipList({
  allergens,
  selectedAllergenIds,
  onSelectAllergen,
  isOptional = false,
}: {
  allergens: DishAllergen[];
  selectedAllergenIds: string[];
  onSelectAllergen: (allergen: DishAllergen) => void;
  isOptional?: boolean;
}) {
  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {allergens.map((allergen) => (
        <AllergenChip
          key={allergen.igfdTaxAllergenId}
          allergenName={allergen.allergenName}
          action="select"
          hasAllergenIcon={false}
          hasActiveColor
          enabled
          isOptional={isOptional}
          selected={selectedAllergenIds.includes(allergen.igfdTaxAllergenId)}
          onClick={() => onSelectAllergen(allergen)}
          onDelete={() => {}}
        />
      ))}
    </Stack>
  );
}

/**
 * Displays allergens associated with a dish, grouped into:
 * - Required allergens (always present)
 * - Removable allergens (optional products)
 * - May contain allergens (cross-contact risk)
 *
 * Users can select allergens to see related products and ingredients.
 */
export const AllergenList = ({
  allergens,
  mayContainAllergens,
  selectedAllergenIds,
  onSelectAllergen,
}: AllergenListProps) => {
  const {
    palette: { oscar },
  } = useTheme();

  // Sort all allergens alphabetically by name
  const sortedAllergens = useMemo(() => {
    return (
      allergens?.sort((a, b) => a.allergenName.localeCompare(b.allergenName)) ||
      []
    );
  }, [allergens]);

  // Filter allergens flagged as optional (i.e., can be removed from the dish)
  const optionalAllergens = useMemo(() => {
    return sortedAllergens.filter((allergen) => allergen.isOptional);
  }, [sortedAllergens]);

  // Filter allergens that are required (not removable)
  const requiredAllergens = useMemo(() => {
    return sortedAllergens.filter((allergen) => !allergen.isOptional);
  }, [sortedAllergens]);

  // Sort may-contain allergens alphabetically
  const sortedMayContainAllergens = useMemo(() => {
    return (
      mayContainAllergens?.sort((a, b) =>
        a.allergenName.localeCompare(b.allergenName)
      ) || []
    );
  }, [mayContainAllergens]);

  return (
    <Stack
      sx={{
        backgroundColor: oscar.light.surface.neutral,
        borderRadius: 4,
        gap: 2,
        padding: 3,
        width: "100%",
      }}
    >
      {/* An empty card is an empty grey box, and on a safety surface an absence
          reads as nobody having looked. Say it. */}
      {sortedAllergens.length === 0 &&
      sortedMayContainAllergens.length === 0 ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <NotInterestedRounded sx={{ color: oscar.light.icon.mid }} />
          <Typography
            variant="labelRMedium"
            color={oscar.light.content.content1}
          >
            No detected allergens
          </Typography>
        </Stack>
      ) : null}

      {/* Render primary allergen section (required + removable) */}
      {sortedAllergens.length > 0 && (
        <>
          <Stack direction="column" alignItems="left" gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <NotInterestedRounded sx={{ color: oscar.light.icon.danger }} />
              <Typography variant="labelRMedium">
                {`${sortedAllergens.length} Detected allergen${
                  sortedAllergens.length > 1 ? "s" : ""
                }`}
              </Typography>
            </Stack>
            <Typography
              variant="labelS"
              sx={{ color: oscar.light.content.content2, fontStyle: "italic" }}
            >
              Select an allergen to see related products and ingredients.
            </Typography>
          </Stack>

          {requiredAllergens.length > 0 && (
            <>
              {optionalAllergens.length > 0 && (
                <AllergenSectionHeader
                  count={requiredAllergens.length}
                  label="Required allergen"
                />
              )}
              <AllergenChipList
                allergens={requiredAllergens}
                selectedAllergenIds={selectedAllergenIds}
                onSelectAllergen={onSelectAllergen}
              />
            </>
          )}

          {optionalAllergens.length > 0 && (
            <Stack direction={"column"} mt={1} gap={"inherit"}>
              <AllergenSectionHeader
                count={optionalAllergens.length}
                label="Removable allergen"
              />
              <AllergenChipList
                allergens={optionalAllergens}
                selectedAllergenIds={selectedAllergenIds}
                onSelectAllergen={onSelectAllergen}
                isOptional
              />
            </Stack>
          )}
        </>
      )}

      {/* Render may contain allergens section if present */}
      {sortedMayContainAllergens.length > 0 && (
        <>
          <Typography
            mt={2}
            variant="labelS"
            sx={{ color: oscar.light.content.content1 }}
          >
            {`May contain ${sortedMayContainAllergens.length} allergen${
              sortedMayContainAllergens.length > 1 ? "s" : ""
            }`}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {sortedMayContainAllergens.map((allergen) => (
              <AllergenChipOutlined
                key={allergen.igfdTaxAllergenId}
                allergenName={allergen.allergenName}
                mayContain
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
};
